/** SUNTRONIX ONE — Sticky Notes floating widget. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StickyNote, Plus, Pin, Trash2, X, Users2, Lock } from "lucide-react";

const COLORS = ["yellow", "blue", "green", "pink", "purple", "orange"];
const COLOR_BG = {
  yellow: "bg-yellow-100 border-yellow-300",
  blue:   "bg-blue-100 border-blue-300",
  green:  "bg-emerald-100 border-emerald-300",
  pink:   "bg-pink-100 border-pink-300",
  purple: "bg-purple-100 border-purple-300",
  orange: "bg-orange-100 border-orange-300",
};


export default function StickyNotesWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("private");
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [draftColor, setDraftColor] = useState("yellow");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/sticky-notes", { params: { scope: tab } });
      setNotes(data.items || []);
    } catch (e) { /* silent — widget shouldn't scream */ }
    finally { setBusy(false); }
  }, [tab]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const create = async () => {
    if (!draftBody.trim()) return;
    try {
      await api.post("/sticky-notes", {
        body: draftBody, color: draftColor, visibility: tab,
        master_company_id: tab === "shared" ? (user?.master_company_id || null) : null,
      });
      setDrafting(false); setDraftBody(""); setDraftColor("yellow");
      toast.success("Note saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const togglePin = async (n) => {
    try { await api.put(`/sticky-notes/${n.id}`, { pinned: !n.pinned }); load(); }
    catch (e) { toast.error("Pin failed"); }
  };
  const del = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/sticky-notes/${n.id}`); load(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const cycleColor = async (n) => {
    const next = COLORS[(COLORS.indexOf(n.color) + 1) % COLORS.length];
    try { await api.put(`/sticky-notes/${n.id}`, { color: next }); load(); }
    catch (e) { /* ignore */ }
  };

  return (
    <>
      {/* Floating trigger */}
      <button onClick={() => setOpen(!open)}
        data-testid="sticky-notes-fab"
        className="fixed bottom-20 md:bottom-6 right-5 md:right-6 z-fab h-12 w-12 rounded-full bg-slate-900 text-white
          shadow-lg hover:bg-slate-800 grid place-items-center transition-all">
        <StickyNote className="h-5 w-5" strokeWidth={1.8} />
      </button>

      {open && (
        <div className="fixed bottom-36 md:bottom-24 right-3 md:right-6 z-fab w-96 max-w-[calc(100vw-1.5rem)] max-h-[70vh] bg-white border border-slate-200
          rounded-lg shadow-2xl flex flex-col" data-testid="sticky-notes-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <StickyNote className="h-4 w-4 text-slate-700" />
              <span className="text-[13px] font-semibold text-slate-900">Sticky notes</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-900">
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            {[
              { id: "private", label: "Personal", icon: Lock },
              { id: "shared",  label: "Shared",   icon: Users2 },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`sticky-tab-${t.id}`}
                  className={
                    "flex-1 h-9 text-[12px] font-medium flex items-center justify-center gap-1.5 " +
                    (tab === t.id
                      ? "text-slate-900 border-b-2 border-slate-900 bg-slate-50"
                      : "text-slate-500 hover:text-slate-800")
                  }>
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
          {/* Body */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {drafting ? (
              <div className={"border rounded-md p-2.5 " + (COLOR_BG[draftColor] || COLOR_BG.yellow)}>
                <textarea autoFocus value={draftBody}
                  onChange={(e) => setDraftBody(e.target.value)}
                  placeholder="Write a note…"
                  data-testid="sticky-draft-body"
                  className="w-full h-24 text-[13px] bg-transparent resize-none focus:outline-none" />
                <div className="flex items-center justify-between mt-1">
                  <div className="flex gap-1">
                    {COLORS.map((c) => (
                      <button key={c} onClick={() => setDraftColor(c)}
                        className={"h-4 w-4 rounded-full border " +
                          COLOR_BG[c] + (draftColor === c ? " ring-2 ring-slate-900" : "")} />
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 text-slate-500"
                      onClick={() => { setDrafting(false); setDraftBody(""); }}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={create} disabled={!draftBody.trim()}
                      className="h-7 bg-slate-900 text-white hover:bg-slate-800"
                      data-testid="sticky-save">
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <button onClick={() => setDrafting(true)}
                data-testid="sticky-add"
                className="w-full h-9 border border-dashed border-slate-300 rounded-md
                  text-[12px] text-slate-500 hover:border-slate-500 hover:text-slate-900
                  flex items-center justify-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> New {tab === "shared" ? "shared" : "personal"} note
              </button>
            )}
            {busy && notes.length === 0 ? (
              <div className="text-center text-[12px] text-slate-500 py-6">Loading…</div>
            ) : notes.length === 0 && !drafting ? (
              <div className="text-center text-[12px] text-slate-500 py-6">
                No {tab} notes yet.
              </div>
            ) : notes.map((n) => (
              <div key={n.id}
                data-testid={`sticky-note-${n.id}`}
                className={"border rounded-md p-2.5 relative group " +
                  (COLOR_BG[n.color] || COLOR_BG.yellow)}>
                <div className="text-[13px] text-slate-800 whitespace-pre-wrap break-words">
                  {n.body}
                </div>
                <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-black/5">
                  <div className="text-[10px] text-slate-500">
                    {n.owner_name || ""} · {(n.updated_at || "").slice(0, 10)}
                  </div>
                  <div className="flex gap-0.5 opacity-70 group-hover:opacity-100">
                    <button onClick={() => cycleColor(n)}
                      className="h-6 w-6 rounded hover:bg-black/10 grid place-items-center"
                      title="Change colour">
                      <div className={"h-3 w-3 rounded-full " + (COLOR_BG[n.color] || COLOR_BG.yellow)} />
                    </button>
                    <button onClick={() => togglePin(n)}
                      data-testid={`sticky-pin-${n.id}`}
                      className={"h-6 w-6 rounded hover:bg-black/10 grid place-items-center " +
                        (n.pinned ? "text-slate-900" : "text-slate-400")}
                      title={n.pinned ? "Unpin" : "Pin"}>
                      <Pin className="h-3 w-3" />
                    </button>
                    <button onClick={() => del(n)}
                      data-testid={`sticky-del-${n.id}`}
                      className="h-6 w-6 rounded hover:bg-black/10 grid place-items-center text-slate-400 hover:text-rose-700"
                      title="Delete">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
